'use client';

import React, { useMemo } from 'react';

import {
    Box,
    BoxContent,
    RadioTile,
    Row,
    Rows,
    SpacerSize,
    Sticker,
    Text,
    TextLevels,
    TypographyBold,
} from '@bytel/trilogy-react';

import { FundingType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';

import { ResponsiveSpacer } from '@components/responsive-spacer';

import { hasInstallment, isCofidis, setSessionFunding } from '@helpers/funding';
import { isPlanPremium } from '@helpers/product';
import { formatPrice } from '@helpers/wording';

import { PricesDetails, sendProductPhoneFundingTracking } from '@product-detail/helpers';

import { FundingRadio } from './funding/funding-radio';
import { FundingRadioInstallmentSubtitle, getFundingRadioLabel, getFundingSubtitle } from './funding/funding-subtitle';
import { OdrInfos } from './funding/odr-infos';

export type SelectFundingProps = {
    currentPlan?: MobilePlanType;
    currentFunding?: FundingType;
    setCurrentFunding: (funding?: FundingType) => void;
    isRenewal: boolean;
    prices: PricesDetails;
    withoutPlanPrice: number;
};

export function SelectFunding({
    currentPlan,
    setCurrentFunding,
    isRenewal,
    prices,
    currentFunding,
    withoutPlanPrice,
}: SelectFundingProps) {
    const handleSelectFunding = (funding: FundingType) => {
        setSessionFunding(funding.type);
        sendProductPhoneFundingTracking(isRenewal, 'Oui');
        setCurrentFunding(funding);
    };

    const hasOdr = useMemo(() => Math.abs(prices.productPrices.odrRefund) > 0, [prices.productPrices.odrRefund]);
    const installmentFundings = useMemo(
        () => currentPlan?.fundings?.filter((plan) => hasInstallment(plan.type)) || [],
        [currentPlan],
    );

    const minimumInitialContributionInstallment = useMemo(
        () =>
            installmentFundings
                ?.map((f) => f.initialContributionWithOdr || f.initialDeposit)
                .sort((a, b) => a - b)[0] || 1,
        [installmentFundings],
    );

    const fundingsToDisplay = useMemo(() => {
        if (!currentPlan?.fundings) return [];

        const favoriteInstallment = currentPlan.fundings.find(
            (funding) => funding.favorite && hasInstallment(funding.type),
        );

        return currentPlan.fundings.filter((funding) => {
            if (isCofidis(funding.type)) return false;
            if (!hasInstallment(funding.type)) return true;

            if (favoriteInstallment) {
                return funding.type === favoriteInstallment.type;
            } else {
                const maxInstallment = installmentFundings.sort((a, b) => b.installmentCount - a.installmentCount)[0];
                return funding.type === maxInstallment?.type;
            }
        });
    }, [currentPlan?.fundings, installmentFundings]);

    if (!currentPlan) {
        return (
            <RadioTile
                description={
                    <>
                        En une seule fois
                        {hasOdr ? (
                            <OdrInfos
                                odrRefund={Math.abs(prices.productPrices.odrRefund)}
                                phonePriceWithOdrDeducted={withoutPlanPrice}
                            />
                        ) : null}
                    </>
                }
                checked
                id='no-funding'
                label={formatPrice(withoutPlanPrice)}
                horizontal
            />
        );
    }
    if ((fundingsToDisplay?.length || 0) === 0) return null;

    return (
        <>
            <Rows gap={4}>
                {isRenewal && !isPlanPremium(currentPlan.typeId) && (
                    <Row>
                        <Box flat>
                            <BoxContent>
                                <Sticker small label='BON À SAVOIR' />
                                <Text level={TextLevels.THREE} marginless>
                                    Bénéficiez d&apos;un prix avantageux sur ce téléphone en changeant de forfait à
                                    l&apos;étape suivante.
                                </Text>
                            </BoxContent>
                        </Box>
                    </Row>
                )}
                {fundingsToDisplay?.map((funding: FundingType) => (
                    <FundingRadio
                        name='funding'
                        key={funding.type}
                        funding={funding}
                        handleSelectFunding={handleSelectFunding}
                        isChecked={
                            hasInstallment(funding.type) && currentFunding
                                ? hasInstallment(currentFunding.type)
                                : currentFunding?.type === funding.type
                        }
                        subtitle={getFundingSubtitle(
                            funding,
                            installmentFundings,
                            hasOdr,
                            prices,
                            currentFunding?.type === funding.type,
                        )}
                        label={getFundingRadioLabel(
                            funding,
                            minimumInitialContributionInstallment,
                            hasInstallment(funding.type),
                        )}
                    />
                ))}
            </Rows>
            {currentFunding && hasInstallment(currentFunding.type) ? (
                <>
                    <ResponsiveSpacer desktopSize={SpacerSize.SIX} mobileSize={SpacerSize.FIVE} />
                    <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        En combien de fois&nbsp;?
                    </Text>
                    <Rows gap={4}>
                        {installmentFundings.map((funding) => (
                            <FundingRadio
                                name='funding-duration'
                                key={funding.type}
                                funding={funding}
                                handleSelectFunding={handleSelectFunding}
                                isChecked={currentFunding?.type === funding.type}
                                label={`En ${funding.installmentCount} fois${funding.interestAmount > 0 ? '' : ' à 0%'}`}
                                subtitle={
                                    <FundingRadioInstallmentSubtitle
                                        funding={funding}
                                        prices={prices}
                                        hasOdr={hasOdr}
                                        currentFunding={currentFunding}
                                    />
                                }
                                showFundingDetails
                            />
                        ))}
                    </Rows>
                </>
            ) : null}
        </>
    );
}
